import React, { useState } from 'react';
import { Select } from 'antd';
import _ from 'lodash';

import { observer } from 'mobx-react';
import { MaterialProps } from '@partsbadger/types';
import { LabeledValue, SelectProps } from 'antd/es/select';
import Suggestion from '../Suggestion';
import QuoteStore from '../../stores/QuoteStore';

interface Props extends SelectProps {
    onChange?: any;
    materials: Array<MaterialProps>;
    suggestions?: {
        id: number;
        name: string;
        thumbnail: string;
    }[];
    manufacturing_type?: string;
    value?: LabeledValue | null;
    for_staff?: boolean;
}

export const Materials = observer((props: Props) => {
    const manufacturing_type = props?.manufacturing_type;

    const [isDropDownMenuOpen, setIsDropDownMenuOpen] = useState(false);

    let data = props.materials;

    if (manufacturing_type) {
        data = data.filter(item => {
            return item.manufacturing_types.some(
                type => type.toLowerCase() === manufacturing_type.toLowerCase()
            );
        });
    }

    if (QuoteStore?.quote?.contact?.is_hobbyist) {
        data = data?.filter(item => {
            return item.name !== 'Review 2D';
        });
    }

    const custom = data.find(item => item.name === 'Custom');

    // Exclude custom from list
    const items = data.filter(item => item.id !== custom?.id);

    const materials_grouped = _.groupBy(items, (item: MaterialProps) => item.material_type);

    const menu = _.map(materials_grouped, (items: Array<MaterialProps>, key: string) => {
        const label = key !== 'null' ? key : 'Uncategorized';
        return (
            <Select.OptGroup key={key} label={label}>
                {items.map(material => (
                    <Select.Option key={material.id}>{material.name}</Select.Option>
                ))}
            </Select.OptGroup>
        );
    });

    const EmptyTag = (
        <div
            className="flex flex-no-wrap p-2 cursor-pointer"
            onClick={() => {
                if (custom && props.onChange) {
                    props.onChange(
                        {
                            key: custom.id.toString(),
                            label: custom.name,
                        },
                        []
                    );
                    setIsDropDownMenuOpen(false);
                }
            }}
        >
            <span className="flex flex-row items-center cursor-pointer justify-center">
                Can't find your material? please select <div className="ml-2 link">Custom</div>
            </span>
        </div>
    );

    return (
        <>
            <Select
                data-testid="material-select"
                showSearch
                labelInValue
                size={props?.for_staff ? 'middle' : 'small'}
                bordered={true}
                placeholder="Select"
                allowClear={false}
                filterOption={(input, option: any) => {
                    try {
                        if (!option.key) {
                            // Filter by group label
                            return option.props?.label?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
                        }

                        return option.props?.children?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
                    } catch (e) {
                        return false;
                    }
                }}
                notFoundContent={EmptyTag}
                dropdownMatchSelectWidth={false}
                style={{ width: '100%' }}
                {...props}
                open={isDropDownMenuOpen}
                onDropdownVisibleChange={visible => setIsDropDownMenuOpen(visible)}
                dropdownRender={menu => menu}
            >
                {!QuoteStore?.quote?.contact?.is_hobbyist && custom && custom?.id > 0 && (
                    <Select.OptGroup label={'Custom'}>
                        <Select.Option key={custom.id}>{custom.name}</Select.Option>
                    </Select.OptGroup>
                )}
                {menu}
            </Select>

            {!props.value?.label && (
                <div className="overflow-auto" style={{ maxHeight: '75px' }}>
                    {props.suggestions?.map(suggestion => (
                        <Suggestion
                            key={suggestion.id}
                            title={suggestion.name}
                            thumbnail={suggestion.thumbnail}
                            handleClick={() => {
                                const val = {
                                    key: suggestion.id.toString(),
                                    label: suggestion.name,
                                    value: suggestion.id.toString(),
                                };

                                if (props.onSelect) {
                                    props.onSelect(val, val);
                                }

                                if (props.onChange) {
                                    props.onChange(val, []);
                                }
                            }}
                        />
                    ))}
                </div>
            )}
        </>
    );
});
